/**
 * Multi-ball tracker using Kalman filter + Hungarian assignment.
 * Maintains stable IDs for up to 3 balls across frames.
 */
import { KalmanFilter2D } from './kalman';
import { greedyAssignment } from './hungarian';
import type { YoloDetection } from './YoloBallDetector';

export interface BallTrack {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  framesSinceSeen: number;
  consecutiveUnmatched: number;
}

const MAX_TRACKS = 3;
const MAX_UNMATCHED_FRAMES = 10;
const MIN_STABLE_FRAMES = 2;
const MATCH_MAX_COST = 120;

interface InternalTrack {
  kf: KalmanFilter2D;
  id: number;
  stableFrames: number;
  consecutiveUnmatched: number;
}

export class BallTracker {
  private tracks: InternalTrack[] = [];
  private nextId = 1;
  private pending: Array<{ x: number; y: number; frameCount: number }> = [];

  update(detections: YoloDetection[]): BallTrack[] {
    const predictions = this.tracks.map((t) => {
      const [x, y, vx, vy] = t.kf.predict();
      return { x, y, vx, vy };
    });

    const costMatrix = this.tracks.map((_, i) =>
      detections.map((d) => {
        const cx = d.x + d.w / 2;
        const cy = d.y + d.h / 2;
        const dx = predictions[i].x - cx;
        const dy = predictions[i].y - cy;
        return Math.sqrt(dx * dx + dy * dy);
      })
    );

    const n = this.tracks.length;
    const m = detections.length;
    const paddedCost: number[][] = [];
    for (let i = 0; i < Math.max(n, m); i++) {
      paddedCost[i] = [];
      for (let j = 0; j < Math.max(n, m); j++) {
        paddedCost[i][j] = i < n && j < m ? costMatrix[i][j] : 1e6;
      }
    }

    const assignment = n > 0 && m > 0 ? greedyAssignment(paddedCost, MATCH_MAX_COST) : [];

    const used = new Set<number>();
    const nextTracks: InternalTrack[] = [];

    for (let i = 0; i < this.tracks.length; i++) {
      const j = assignment[i];
      const track = this.tracks[i];
      if (j >= 0 && j < detections.length && !used.has(j)) {
        const d = detections[j];
        const cx = d.x + d.w / 2;
        const cy = d.y + d.h / 2;
        track.kf.update(cx, cy);
        used.add(j);
        nextTracks.push({
          kf: track.kf,
          id: track.id,
          stableFrames: track.stableFrames + 1,
          consecutiveUnmatched: 0,
        });
      } else {
        track.kf.predict();
        const unmatched = track.consecutiveUnmatched + 1;
        if (unmatched < MAX_UNMATCHED_FRAMES && track.stableFrames >= MIN_STABLE_FRAMES) {
          nextTracks.push({
            kf: track.kf,
            id: track.id,
            stableFrames: track.stableFrames,
            consecutiveUnmatched: unmatched,
          });
        }
      }
    }

    for (let j = 0; j < detections.length; j++) {
      if (used.has(j)) continue;
      const d = detections[j];
      const cx = d.x + d.w / 2;
      const cy = d.y + d.h / 2;

      const existingPending = this.pending.find(
        (p) => Math.hypot(p.x - cx, p.y - cy) < MATCH_MAX_COST
      );
      if (existingPending) {
        existingPending.x = cx;
        existingPending.y = cy;
        existingPending.frameCount++;
        if (existingPending.frameCount >= MIN_STABLE_FRAMES && nextTracks.length < MAX_TRACKS) {
          this.pending = this.pending.filter((p) => p !== existingPending);
          nextTracks.push({
            kf: new KalmanFilter2D(cx, cy),
            id: this.nextId++,
            stableFrames: 1,
            consecutiveUnmatched: 0,
          });
        }
      } else {
        this.pending.push({ x: cx, y: cy, frameCount: 1 });
      }
    }

    this.pending = this.pending.filter((p) => {
      const nearby = detections.some((d) => {
        const cx = d.x + d.w / 2;
        const cy = d.y + d.h / 2;
        return Math.hypot(p.x - cx, p.y - cy) < MATCH_MAX_COST * 2;
      });
      return p.frameCount < 10 && nearby;
    });

    this.tracks = nextTracks.slice(0, MAX_TRACKS);

    return this.tracks.map((t) => {
      const [x, y, vx, vy] = t.kf.getState();
      return {
        id: t.id,
        x,
        y,
        vx,
        vy,
        framesSinceSeen: t.consecutiveUnmatched,
        consecutiveUnmatched: t.consecutiveUnmatched,
      };
    });
  }
}
